import type { Appointment } from "@/types/admin-portal/appointment";
import {
	type BlockReason,
	blockReason,
	firstReason,
	packageStoppedReason,
	REASON,
} from "./details/action-buttons";

// The per-visit hash PreparationOrdersAppointmentService builds for the Orders list — not
// a full Appointment, so only the fields it really carries are read here.
export interface OrderVisit {
	visitNumber: number;
	status: Appointment["status"];
	hasSoap: boolean;
	hasEvidence: boolean;
	isFinalVisit: boolean;
	therapistName: string | null;
}

export interface OrderGateInput {
	// Order#status is a plain string column, so it arrives UPPERCASE (see AGENTS.md).
	status: string;
	firstAppointmentId: string | null;
	appointments: OrderVisit[];
	mitraVisit?: boolean;
	// The Mitra visits the reason dialog may change; empty for a customer's order no admin gave a reason.
	mitraVisits?: unknown[];
	creatorType?: CreatorType;
}

// Who booked the order. "admin" means it came from an AppointmentDraft (Order.filter_by_creator).
export type CreatorType = "admin" | "customer";

const R = "button.disabled_reason";

export const ORDER_REASON = {
	noVisits: `${R}.no_visits`,
	allVisitsLocked: `${R}.all_visits_locked`,
	noVisitWithSoap: `${R}.no_visit_with_soap`,
	noVisitWithEvidence: `${R}.no_visit_with_evidence`,
	visitsNotCompleted: `${R}.visits_not_completed`,
	finalSoapNoTherapist: `${R}.final_soap_no_therapist`,
	finalSoapNoSoap: `${R}.final_soap_no_soap`,
	finalSoapNotFinal: `${R}.final_soap_not_final`,
	noMitraVisit: `${R}.no_mitra_visit`,
	noAppointmentDraft: `${R}.no_appointment_draft`,
	noVisitCompleted: `${R}.no_visit_completed`,
	allVisitsCompleted: `${R}.all_visits_completed`,
} as const;

const ORDER_STATUS_REASON: Record<string, string> = {
	COMPLETED: `${R}.order_completed`,
	PARTIALLY_COMPLETED: `${R}.order_partially_completed`,
	REFUNDED: `${R}.order_refunded`,
	CANCELLED: `${R}.order_cancelled`,
	DRAFT: `${R}.order_draft`,
};

// PARTIALLY_COMPLETED is a stopped package: settled like COMPLETED, so it closes
// everything COMPLETED closes.
type ClosingStatus =
	| "COMPLETED"
	| "PARTIALLY_COMPLETED"
	| "REFUNDED"
	| "CANCELLED"
	| "DRAFT";

const statusReason = (status: string, closing: ClosingStatus[]): BlockReason =>
	(closing as string[]).includes(status) ? ORDER_STATUS_REASON[status] : null;

const asAppointment = (visit: OrderVisit) => visit as unknown as Appointment;

// An order-level entry over the per-visit items: closed when the order has no visits, or
// when no visit passes `open` — and then it says so for the order as a whole.
const anyVisitReason = (
	visits: OrderVisit[],
	open: (visit: OrderVisit) => boolean,
	noneOpen: string,
): BlockReason => {
	if (!visits.length) return ORDER_REASON.noVisits;
	return visits.some(open) ? null : noneOpen;
};

const unlocked =
	(gate: (appt: Appointment) => BlockReason) => (v: OrderVisit) =>
		!gate(asAppointment(v));

// Every gate in the Orders row menu, as the reason it is closed (null when open). Each
// one keeps the rule it had as a boolean; only the answer gained a why. Causes are passed
// in priority order: whitelist → order status → visit lock → missing document → the rest.
export const orderBlockReasons = (
	order: OrderGateInput,
	canUpdateSoap: boolean,
) => {
	const visits = order.appointments ?? [];
	const noFirstVisit = order.firstAppointmentId ? null : ORDER_REASON.noVisits;
	const noVisits = visits.length ? null : ORDER_REASON.noVisits;

	const lastVisit = visits.reduce<OrderVisit | undefined>(
		(last, current) =>
			!last || current.visitNumber > last.visitNumber ? current : last,
		undefined,
	);

	return {
		updatePic: noFirstVisit,
		// The order's own status does not gate these — reopening a cancelled, completed or
		// refunded order is a real thing staff do, and the backend allows it. What locks a
		// visit is its own finished paperwork, so the order-level entries only close when
		// every visit is locked; the per-visit items gate themselves.
		reschedule: anyVisitReason(
			visits,
			unlocked(blockReason.reschedule),
			ORDER_REASON.allVisitsLocked,
		),
		// Except a stopped package. An OrderVisit carries no order, so the per-visit gate
		// cannot see the stop and it is checked here.
		updateStatus: firstReason(
			packageStoppedReason(order.status),
			anyVisitReason(
				visits,
				unlocked(blockReason.updateStatus),
				ORDER_REASON.allVisitsLocked,
			),
		),
		cancelBooking: firstReason(
			statusReason(order.status, [
				"COMPLETED",
				"PARTIALLY_COMPLETED",
				"REFUNDED",
				"CANCELLED",
			]),
			noFirstVisit,
		),
		updatePayment: firstReason(
			statusReason(order.status, [
				"COMPLETED",
				"PARTIALLY_COMPLETED",
				"REFUNDED",
				"CANCELLED",
			]),
			noVisits,
		),
		changePackage: statusReason(order.status, [
			"COMPLETED",
			"PARTIALLY_COMPLETED",
			"REFUNDED",
		]),
		// Same gate as change-package. The VOUCHER_ENABLE flag is NOT part of it: with the
		// flag down the backend still honours a removal, and the form disables the apply
		// half on its own.
		changeVoucher: statusReason(order.status, [
			"COMPLETED",
			"PARTIALLY_COMPLETED",
			"REFUNDED",
		]),
		changeMitraReason: firstReason(
			order.mitraVisit ? null : ORDER_REASON.noMitraVisit,
			order.mitraVisits?.length === 0 ? ORDER_REASON.noAppointmentDraft : null,
		),
		// Deliberately the ORDER's status rather than the per-visit document lock: once the
		// order itself is settled, the record it was booked against stops being editable.
		updateAddress: firstReason(
			statusReason(order.status, [
				"COMPLETED",
				"PARTIALLY_COMPLETED",
				"REFUNDED",
			]),
			noFirstVisit,
		),
		// The order's status still closes it, and so does every visit being locked.
		editMedicalRecord: firstReason(
			statusReason(order.status, [
				"COMPLETED",
				"PARTIALLY_COMPLETED",
				"REFUNDED",
			]),
			anyVisitReason(
				visits,
				unlocked(blockReason.editMedicalRecord),
				ORDER_REASON.allVisitsLocked,
			),
		),
		// No order-status gate: a finished order is exactly the one whose notes still need
		// correcting. Upstream answers 404 for a visit with no SOAP.
		editSoap: firstReason(
			canUpdateSoap ? null : REASON.soapNotAllowed,
			anyVisitReason(visits, (v) => v.hasSoap, ORDER_REASON.noVisitWithSoap),
		),
		deleteSoap: firstReason(
			canUpdateSoap ? null : REASON.deleteNotAllowed,
			anyVisitReason(visits, (v) => v.hasSoap, ORDER_REASON.noVisitWithSoap),
		),
		deleteEvidence: firstReason(
			canUpdateSoap ? null : REASON.deleteNotAllowed,
			anyVisitReason(
				visits,
				(v) => v.hasEvidence,
				ORDER_REASON.noVisitWithEvidence,
			),
		),
		regenerateInvoice: statusReason(order.status, [
			"COMPLETED",
			"PARTIALLY_COMPLETED",
		]),
		sendFeedbackReminder:
			noVisits ??
			(visits.every((v) => v.status === "completed")
				? null
				: ORDER_REASON.visitsNotCompleted),
		// Upstream builds the final report from the last visit only, and refuses it unless
		// that visit has a therapist and a SOAP marked as the final visit.
		downloadFinalSoap: !lastVisit
			? ORDER_REASON.noVisits
			: firstReason(
					lastVisit.therapistName ? null : ORDER_REASON.finalSoapNoTherapist,
					lastVisit.hasSoap ? null : ORDER_REASON.finalSoapNoSoap,
					lastVisit.isFinalVisit ? null : ORDER_REASON.finalSoapNotFinal,
				),
		// Hentikan Paket needs some visits done and some not. The rest may all be refunded
		// or cancelled by hand already: that order sits in SCHEDULED until it is stopped,
		// so it is the main case, not an edge.
		stopPackage: firstReason(
			statusReason(order.status, [
				"COMPLETED",
				"PARTIALLY_COMPLETED",
				"REFUNDED",
				"CANCELLED",
				"DRAFT",
			]),
			noVisits,
			visits.some(isCompleted) ? null : ORDER_REASON.noVisitCompleted,
			visits.every(isCompleted) ? ORDER_REASON.allVisitsCompleted : null,
		),
	};
};

const isCompleted = (visit: OrderVisit) => visit.status === "completed";
